import { useMemo, type ComponentPropsWithRef, type ReactElement } from 'react'

import { cn } from './cn'

/**
 * Every icon is a Google Material Symbol (Outlined), loaded as a font in `index.html`, as the
 * mockups' `components/ui/icons.jsx` does. Each export keeps the mockups' name and renders the
 * symbol's ligature. An icon is decorative (`aria-hidden`) unless it is given an `aria-label`.
 */
export interface IconProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /** In px. */
  readonly size?: number
  /** The symbol's filled variant: a font glyph ignores CSS `fill`, so the FILL axis is set. */
  readonly filled?: boolean
}

export type IconComponent = (props: IconProps) => ReactElement

const filledSettings = "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"

function MaterialSymbol(props: IconProps & { readonly name: string }) {
  const { name, size = 20, filled = false, className, style, ...rest } = props
  const label = rest['aria-label']
  const sized = useMemo(
    () => ({
      fontSize: size,
      width: size,
      height: size,
      ...(filled && { fontVariationSettings: filledSettings }),
      ...style,
    }),
    [size, filled, style],
  )

  return (
    <span
      aria-hidden={label === undefined ? true : undefined}
      role={label === undefined ? undefined : 'img'}
      {...rest}
      className={cn('material-symbols-outlined', className)}
      style={sized}
    >
      {name}
    </span>
  )
}

function make(name: string, displayName: string): IconComponent {
  const Icon = (props: IconProps) => <MaterialSymbol name={name} {...props} />
  Icon.displayName = displayName
  return Icon
}

// The mockups' list, in their order.
export const Activity = make('monitoring', 'Activity')
export const AlertCircle = make('error', 'AlertCircle')
export const AlertTriangle = make('warning', 'AlertTriangle')
export const ArrowLeft = make('arrow_back', 'ArrowLeft')
export const ArrowUpDown = make('swap_vert', 'ArrowUpDown')
export const ArrowUpRight = make('arrow_outward', 'ArrowUpRight')
export const Award = make('workspace_premium', 'Award')
export const BadgeCheck = make('verified', 'BadgeCheck')
export const BarChart3 = make('bar_chart', 'BarChart3')
export const Bell = make('notifications', 'Bell')
export const BookOpen = make('menu_book', 'BookOpen')
export const Bookmark = make('bookmark', 'Bookmark')
export const Briefcase = make('work', 'Briefcase')
export const Bug = make('bug_report', 'Bug')
export const Building2 = make('apartment', 'Building2')
export const Calendar = make('calendar_month', 'Calendar')
export const CalendarCheck = make('event_available', 'CalendarCheck')
export const CalendarClock = make('event_note', 'CalendarClock')
export const CalendarDays = make('calendar_view_month', 'CalendarDays')
export const CalendarX = make('event_busy', 'CalendarX')
export const Check = make('check', 'Check')
export const CheckCircle2 = make('check_circle', 'CheckCircle2')
export const CheckSquare = make('check_box', 'CheckSquare')
export const ChevronDown = make('expand_more', 'ChevronDown')
export const ChevronLeft = make('chevron_left', 'ChevronLeft')
export const ChevronRight = make('chevron_right', 'ChevronRight')
export const ChevronUp = make('expand_less', 'ChevronUp')
export const ClipboardCheck = make('assignment_turned_in', 'ClipboardCheck')
export const ClipboardList = make('assignment', 'ClipboardList')
export const Clock = make('schedule', 'Clock')
export const Code2 = make('code', 'Code2')
export const Copy = make('content_copy', 'Copy')
export const DoorOpen = make('meeting_room', 'DoorOpen')
export const Download = make('download', 'Download')
export const DownloadCloud = make('cloud_download', 'DownloadCloud')
export const XCircle = make('cancel', 'XCircle')
export const Eye = make('visibility', 'Eye')
export const EyeOff = make('visibility_off', 'EyeOff')
export const FileCheck2 = make('task', 'FileCheck2')
export const FilePlus2 = make('note_add', 'FilePlus2')
export const FileSpreadsheet = make('table_chart', 'FileSpreadsheet')
export const FileText = make('description', 'FileText')
export const Filter = make('filter_list', 'Filter')
export const Flag = make('flag', 'Flag')
export const FlaskConical = make('science', 'FlaskConical')
export const FolderOpen = make('folder_open', 'FolderOpen')
export const FolderPlus = make('create_new_folder', 'FolderPlus')
export const Frown = make('sentiment_dissatisfied', 'Frown')
export const GitMerge = make('merge', 'GitMerge')
export const Glasses = make('eyeglasses', 'Glasses')
export const Globe = make('language', 'Globe')
export const GraduationCap = make('school', 'GraduationCap')
export const Handshake = make('handshake', 'Handshake')
export const Heart = make('favorite', 'Heart')
export const History = make('history', 'History')
export const Image = make('image', 'Image')
export const Info = make('info', 'Info')
export const KeyRound = make('key', 'KeyRound')
export const Layers = make('layers', 'Layers')
export const LayoutDashboard = make('dashboard', 'LayoutDashboard')
export const LayoutGrid = make('grid_view', 'LayoutGrid')
export const Link2 = make('link', 'Link2')
export const List = make('list', 'List')
export const Lock = make('lock', 'Lock')
export const Mail = make('mail', 'Mail')
export const Megaphone = make('campaign', 'Megaphone')
export const MessageSquare = make('chat_bubble', 'MessageSquare')
export const MessageSquareText = make('chat', 'MessageSquareText')
export const MonitorPlay = make('smart_display', 'MonitorPlay')
export const Moon = make('dark_mode', 'Moon')
export const Network = make('account_tree', 'Network')
export const OctagonAlert = make('report', 'OctagonAlert')
export const Package = make('inventory_2', 'Package')
export const Paintbrush = make('brush', 'Paintbrush')
export const PanelLeftClose = make('left_panel_close', 'PanelLeftClose')
export const PanelLeftOpen = make('left_panel_open', 'PanelLeftOpen')
export const Pencil = make('edit', 'Pencil')
export const Play = make('play_arrow', 'Play')
export const PlayCircle = make('play_circle', 'PlayCircle')
export const Plus = make('add', 'Plus')
export const Presentation = make('co_present', 'Presentation')
export const Puzzle = make('extension', 'Puzzle')
export const RefreshCcw = make('sync', 'RefreshCcw')
export const RotateCcw = make('replay', 'RotateCcw')
export const Route = make('route', 'Route')
export const Save = make('save', 'Save')
export const Search = make('search', 'Search')
export const Send = make('send', 'Send')
export const Settings = make('settings', 'Settings')
export const ShieldCheck = make('verified_user', 'ShieldCheck')
export const ShoppingCart = make('shopping_cart', 'ShoppingCart')
export const Smile = make('sentiment_satisfied', 'Smile')
export const Sparkles = make('auto_awesome', 'Sparkles')
export const Star = make('star', 'Star')
export const Sun = make('light_mode', 'Sun')
export const Target = make('target', 'Target')
export const Ticket = make('confirmation_number', 'Ticket')
export const Trash2 = make('delete', 'Trash2')
export const Trophy = make('emoji_events', 'Trophy')
export const Upload = make('upload', 'Upload')
export const User = make('person', 'User')
export const UserCheck = make('how_to_reg', 'UserCheck')
export const UserPlus = make('person_add', 'UserPlus')
export const Users = make('group', 'Users')
export const UsersRound = make('groups', 'UsersRound')
export const Video = make('videocam', 'Video')
export const X = make('close', 'X')
export const Compass = make('explore', 'Compass')
export const ArrowUp = make('arrow_upward', 'ArrowUp')
export const ArrowDown = make('arrow_downward', 'ArrowDown')
export const ArrowRight = make('arrow_forward', 'ArrowRight')
// The table sort controls; the mockups draw them from the Sharp set, which is not loaded for three glyphs.
export const UnfoldMore = make('unfold_more', 'UnfoldMore')

// Used here and not yet in the mockups.
export const CloudCheck = make('cloud_done', 'CloudCheck')
export const ExternalLink = make('open_in_new', 'ExternalLink')
export const HeartPulse = make('monitor_heart', 'HeartPulse')
export const Inbox = make('inbox', 'Inbox')
// The HTML editor's toolbar (shared/rich-text), whose mockup draws placeholder dots.
export const AlignCenter = make('format_align_center', 'AlignCenter')
export const AlignLeft = make('format_align_left', 'AlignLeft')
export const AlignRight = make('format_align_right', 'AlignRight')
export const Bold = make('format_bold', 'Bold')
export const IndentDecrease = make('format_indent_decrease', 'IndentDecrease')
export const IndentIncrease = make('format_indent_increase', 'IndentIncrease')
export const Italic = make('format_italic', 'Italic')
export const ListBulleted = make('format_list_bulleted', 'ListBulleted')
export const ListOrdered = make('format_list_numbered', 'ListOrdered')
export const Minus = make('horizontal_rule', 'Minus')
export const Redo2 = make('redo', 'Redo2')
export const RemoveFormatting = make('format_clear', 'RemoveFormatting')
export const Undo2 = make('undo', 'Undo2')
